import type { FormikErrors } from 'formik';
import * as React from 'react';
import type { ReactNode } from 'react';
import { NotificationBannerBase } from '../../../NotificationBanner/NotificationBannerBase';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { usePrevious } from '~/Partials/Utils/usePrevious';
import styles from './FormikValidationErrorHandler.module.css';

export interface FormikValidationErrorHandlerProps<TValues> {
    children: ReactNode;
    isSubmitting: boolean;
    isValid: boolean;
    errors?: FormikErrors<TValues>;
    setSelector?: (name: string) => string;
    alertMessage?: (errorsCount: number) => ReactNode;
}

const resolveFieldNames = <TValues,>(errors: FormikErrors<TValues> | undefined, currentPath = '') => {
    const fieldNames: string[] = [];

    if (!errors) {
        return fieldNames;
    }

    Object.keys(errors).forEach(errorKey => {
        const value = errors[errorKey as keyof TValues];

        if (value && typeof value === 'object' && !React.isValidElement(value)) {
            fieldNames.push(...resolveFieldNames(value, `${currentPath}${errorKey}.`));
        } else {
            fieldNames.push(`${currentPath}${errorKey}`);
        }
    });

    return fieldNames;
};

export const FormikValidationErrorHandler = <TValues,>(props: FormikValidationErrorHandlerProps<TValues>) => {
    const { children, isSubmitting, isValid, errors, setSelector, alertMessage } = props;

    const [keys, setKeys] = React.useState<string[]>();
    const [isAlertVisible, setIsAlertVisible] = React.useState(false);
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    const prevIsSubmitting = usePrevious(isSubmitting);

    React.useEffect(() => {
        if (!isSubmitting && prevIsSubmitting && !isValid) {
            const fieldsWithError = resolveFieldNames(errors);
            setKeys(fieldsWithError);

            if (fieldsWithError.length > 0 && wrapperRef.current) {
                setIsAlertVisible(true);

                const key = fieldsWithError[0];

                const selector = setSelector ? setSelector(key) : `[name="${key}"]`;
                const firstErrorElement = wrapperRef.current.querySelector(selector) as HTMLElement;

                if (firstErrorElement) {
                    firstErrorElement.focus();
                }
            }
        }
    }, [isSubmitting, isValid]);

    React.useEffect(() => {
        if (isAlertVisible) {
            const timer = setTimeout(() => {
                setIsAlertVisible(false);
            }, 10000);

            return () => clearTimeout(timer);
        }
    }, [isAlertVisible]);

    return (
        <div ref={wrapperRef}>
            {children}
            {!!alertMessage && keys && keys.length > 0 && (
                <NotificationBannerBase isOpen={isAlertVisible}>
                    <div role="alert" style={{ display: isAlertVisible ? 'flex' : 'none' }}>
                        <SvgIcon className={styles.Icon} iconName="bell" />
                        {alertMessage(keys.length)}
                    </div>
                </NotificationBannerBase>
            )}
        </div>
    );
};
